import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CommercialLayout from "../../components/layouts/CommercialLayout";
import api from "../../services/api";

const couleurScore = (score) => {
  if (score >= 80) return { bg: "#f0fdf4", text: "#16a34a", border: "#86efac", label: "Très fiable" };
  if (score >= 60) return { bg: "#fefce8", text: "#ca8a04", border: "#fde68a", label: "Fiable" };
  if (score >= 40) return { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa", label: "À risque" };
  return { bg: "#fef2f2", text: "#dc2626", border: "#fecaca", label: "Bloqué" };
};

// ── Modal Photo CNI ──
const ModalPhoto = ({ url, label, onClose }) => (
  <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
    <div style={{ position: "absolute", top: "1rem", right: "1rem" }}>
      <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "white", fontSize: "20px", width: "36px", height: "36px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
    </div>
    <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", marginBottom: "0.75rem" }}>{label}</div>
    <img onClick={e => e.stopPropagation()} src={url} alt={label}
      style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "12px" }} />
  </div>
);

// ── Modal Créer Crédit ──
const ModalCreerCredit = ({ clientId, onClose, onSuccess }) => {
  const [montant, setMontant] = useState("");
  const [coupons, setCoupons] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/credits", {
        clientId: parseInt(clientId),
        montant: parseInt(montant),
        coupons: parseInt(coupons),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const plafond = coupons * 1000 * 25;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "16px", padding: "1.5rem", width: "100%", maxWidth: "400px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>Nouveau crédit</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280" }}>×</button>
        </div>
        {error && (
          <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}>{error}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "6px" }}>Nombre de coupons</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {[1, 2, 3, 4].map((n) => (
                <button key={n} type="button" onClick={() => setCoupons(n)} style={{ flex: 1, padding: "0.6rem", border: coupons === n ? "2px solid #185FA5" : "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "500", backgroundColor: coupons === n ? "#E6F1FB" : "white", color: coupons === n ? "#185FA5" : "#6b7280" }}>{n}</button>
              ))}
            </div>
            <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>Plafond pour {coupons} coupon(s) : {plafond.toLocaleString("fr-FR")} F</p>
          </div>
          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "6px" }}>Montant (F CFA) *</label>
            <input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder={`Max ${plafond.toLocaleString("fr-FR")} F`} required min={1} max={plafond}
              style={{ width: "100%", padding: "0.75rem 1rem", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", background: "white" }} />
          </div>
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "0.75rem", background: "linear-gradient(135deg, #1d4ed8, #185FA5)", color: "white", fontWeight: "500", fontSize: "14px", borderRadius: "9999px", border: "none", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Création..." : "Créer le crédit"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Page principale ──
const FicheClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [client, setClient] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModalCredit, setShowModalCredit] = useState(false);
  const [photoModal, setPhotoModal] = useState(null); // { url, label }
  const [rembLoading, setRembLoading] = useState(false);
  const [rembMsg, setRembMsg] = useState("");
  const [error, setError] = useState("");

  const fetchData = async () => {
    try {
      const [clientRes, dashRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/commercials/${utilisateur.id}/dashboard`),
      ]);
      setClient(clientRes.data);
      const clientDash = dashRes.data.clients?.find(c => c.id === parseInt(id));
      setDashboard(clientDash);
    } catch (err) {
      setError("Client introuvable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRembourser = async (creditId) => {
    setRembLoading(true);
    setRembMsg("");
    try {
      await api.post(`/credits/${creditId}/rembourser`, {});
      setRembMsg("Remboursement traité avec succès !");
      fetchData();
    } catch (err) {
      setRembMsg(err.response?.data?.error || "Erreur lors du remboursement");
    } finally {
      setRembLoading(false);
    }
  };

  if (loading) return (
    <CommercialLayout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #E6F1FB", borderTop: "3px solid #185FA5", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    </CommercialLayout>
  );

  if (error) return (
    <CommercialLayout>
      <div style={{ padding: "2rem", textAlign: "center", color: "#dc2626" }}>{error}</div>
    </CommercialLayout>
  );

  const score = dashboard?.score || client?.score || 60;
  const sc = couleurScore(score);
  const creditEnCours = dashboard?.credit_en_cours || null;
  const creditNonRembourse = dashboard?.credit_non_rembourse || null;

  return (
    <CommercialLayout>
      {/* Modal photo CNI */}
      {photoModal && (
        <ModalPhoto url={photoModal.url} label={photoModal.label} onClose={() => setPhotoModal(null)} />
      )}

      {showModalCredit && (
        <ModalCreerCredit clientId={id} onClose={() => setShowModalCredit(false)} onSuccess={() => { setShowModalCredit(false); fetchData(); }} />
      )}

      <button onClick={() => navigate("/commercial/clients")}
        style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "13px", marginBottom: "1.25rem", padding: 0 }}>
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
        Retour aux clients
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "1.25rem", alignItems: "start" }}>
        <div>
          {/* Carte identité client */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.5rem", marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: sc.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: "500", color: sc.text, flexShrink: 0 }}>
                {client?.nom?.charAt(0)}{client?.prenom?.charAt(0)}
              </div>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "500", color: "#111827", margin: 0 }}>{client?.prenom} {client?.nom}</h2>
                <span style={{ fontSize: "12px", padding: "2px 10px", borderRadius: "6px", backgroundColor: sc.bg, color: sc.text }}>{client?.statut}</span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
              {[
                { label: "Téléphone", value: client?.telephone || "—" },
                { label: "Date de naissance", value: client?.date_naissance ? new Date(client.date_naissance).toLocaleDateString("fr-FR") : "—" },
                { label: "Âge", value: client?.age ? `${client.age} ans` : "—" },
                { label: "Sexe", value: client?.sexe === "M" ? "Masculin" : client?.sexe === "F" ? "Féminin" : "—" },
                { label: "CNI", value: client?.cni || "—" },
                { label: "Activité", value: client?.activite || "—" },
                { label: "Localisation", value: client?.localisation || "—" },
              ].map((info, i) => (
                <div key={i}>
                  <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px" }}>{info.label}</div>
                  <div style={{ fontSize: "13px", color: "#111827" }}>{info.value}</div>
                </div>
              ))}
            </div>

            {/* Photos CNI — cliquables */}
            {(client?.photo_cni_recto || client?.photo_cni_verso) && (
              <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem" }}>
                {client?.photo_cni_recto && (
                  <div>
                    <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>CNI Recto</div>
                    <div style={{ position: "relative", cursor: "pointer" }}
                      onClick={() => setPhotoModal({ url: `${process.env.REACT_APP_API_URL}${client.photo_cni_recto}`, label: "CNI Recto" })}>
                      <img src={`${process.env.REACT_APP_API_URL}${client.photo_cni_recto}`} alt="CNI Recto"
                        style={{ width: "120px", height: "75px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb", display: "block" }} />
                      <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.3)"}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = "rgba(0,0,0,0)"}>
                        <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" style={{ opacity: 0.9 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                      </div>
                    </div>
                  </div>
                )}
                {client?.photo_cni_verso && (
                  <div>
                    <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>CNI Verso</div>
                    <div style={{ position: "relative", cursor: "pointer" }}
                      onClick={() => setPhotoModal({ url: `${process.env.REACT_APP_API_URL}${client.photo_cni_verso}`, label: "CNI Verso" })}>
                      <img src={`${process.env.REACT_APP_API_URL}${client.photo_cni_verso}`} alt="CNI Verso"
                        style={{ width: "120px", height: "75px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb", display: "block" }} />
                      <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.3)"}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = "rgba(0,0,0,0)"}>
                        <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" style={{ opacity: 0.9 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {rembMsg && (
            <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", borderRadius: "8px", backgroundColor: rembMsg.includes("succès") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${rembMsg.includes("succès") ? "#86efac" : "#fecaca"}`, color: rembMsg.includes("succès") ? "#16a34a" : "#dc2626", fontSize: "13px" }}>
              {rembMsg}
            </div>
          )}

          {creditEnCours && (
            <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#185FA5" }} />
                Crédit en cours
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1rem" }}>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Montant</div><div style={{ fontSize: "16px", fontWeight: "500" }}>{Number(creditEnCours.montant).toLocaleString("fr-FR")} F</div></div>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Coupons</div><div style={{ fontSize: "16px", fontWeight: "500" }}>{creditEnCours.coupons}</div></div>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Échéance</div><div style={{ fontSize: "13px", fontWeight: "500", color: creditEnCours.minutes_restantes < 0 ? "#dc2626" : "#ea580c" }}>{new Date(creditEnCours.echeance).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div></div>
              </div>
              <button onClick={() => handleRembourser(creditEnCours.id)} disabled={rembLoading} style={{ width: "100%", padding: "0.75rem", background: "linear-gradient(135deg, #16a34a, #15803d)", color: "white", fontWeight: "500", fontSize: "14px", borderRadius: "9999px", border: "none", cursor: rembLoading ? "not-allowed" : "pointer", opacity: rembLoading ? 0.7 : 1 }}>
                {rembLoading ? "Traitement..." : "✓ Rembourser ce crédit"}
              </button>
            </div>
          )}

          {creditNonRembourse && (
            <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#dc2626", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <svg width="14" height="14" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Crédit non remboursé
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1rem" }}>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Montant dû</div><div style={{ fontSize: "16px", fontWeight: "500", color: "#dc2626" }}>{Number(creditNonRembourse.montant).toLocaleString("fr-FR")} F</div></div>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Pénalités</div><div style={{ fontSize: "16px", fontWeight: "500", color: "#dc2626" }}>{Number(creditNonRembourse.penalites || 0).toLocaleString("fr-FR")} F</div></div>
                <div><div style={{ fontSize: "11px", color: "#9ca3af" }}>Depuis</div><div style={{ fontSize: "13px", fontWeight: "500", color: "#dc2626" }}>{new Date(creditNonRembourse.date_non_rembourse).toLocaleDateString("fr-FR")}</div></div>
              </div>
              <button onClick={() => handleRembourser(creditNonRembourse.id)} disabled={rembLoading} style={{ width: "100%", padding: "0.75rem", backgroundColor: "#dc2626", color: "white", fontWeight: "500", fontSize: "14px", borderRadius: "9999px", border: "none", cursor: rembLoading ? "not-allowed" : "pointer", opacity: rembLoading ? 0.7 : 1 }}>
                {rembLoading ? "Traitement..." : "Régulariser ce crédit"}
              </button>
            </div>
          )}

          {!creditEnCours && !creditNonRembourse && client?.statut !== "Bloque" && client?.statut !== "Archive" && (
            <div style={{ backgroundColor: "white", border: "0.5px dashed #e5e7eb", borderRadius: "16px", padding: "2rem", textAlign: "center", marginBottom: "1.25rem" }}>
              <p style={{ color: "#9ca3af", fontSize: "14px", marginBottom: "1rem" }}>Aucun crédit actif</p>
              <button onClick={() => setShowModalCredit(true)} style={{ padding: "0.65rem 1.5rem", background: "linear-gradient(135deg, #1d4ed8, #185FA5)", color: "white", fontWeight: "500", fontSize: "13px", borderRadius: "9999px", border: "none", cursor: "pointer" }}>
                + Créer un crédit
              </button>
            </div>
          )}

          {client?.statut === "Bloque" && !creditNonRembourse && (
            <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "1rem", fontSize: "13px", color: "#dc2626" }}>
              Ce client est bloqué — aucun crédit possible tant que son score est inférieur à 40.
            </div>
          )}
        </div>

        {/* Colonne droite : Score */}
        <div>
          <div style={{ backgroundColor: "white", border: `1px solid ${sc.border}`, borderRadius: "16px", padding: "1.5rem", textAlign: "center" }}>
            <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "0.5rem" }}>Score client</div>
            <div style={{ fontSize: "48px", fontWeight: "500", color: sc.text, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: "13px", color: sc.text, marginTop: "4px", marginBottom: "1rem" }}>{sc.label}</div>
            <div style={{ backgroundColor: "#f3f4f6", borderRadius: "9999px", height: "8px", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${score}%`, backgroundColor: sc.text, borderRadius: "9999px", transition: "width 0.5s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>
              <span>0</span><span>100</span>
            </div>
            <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "6px" }}>
              {[
                { label: "Très fiable", min: 80, color: "#16a34a" },
                { label: "Fiable", min: 60, color: "#ca8a04" },
                { label: "À risque", min: 40, color: "#ea580c" },
                { label: "Bloqué", min: 0, color: "#dc2626" },
              ].map((item) => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#6b7280" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: item.color, flexShrink: 0 }} />
                  {item.label} (≥{item.min})
                </div>
              ))}
            </div>
          </div>

          {!creditEnCours && !creditNonRembourse && client?.statut !== "Bloque" && client?.statut !== "Archive" && (
            <button onClick={() => setShowModalCredit(true)} style={{ width: "100%", marginTop: "0.75rem", padding: "0.75rem", background: "linear-gradient(135deg, #1d4ed8, #185FA5)", color: "white", fontWeight: "500", fontSize: "14px", borderRadius: "9999px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <svg width="14" height="14" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Créer un crédit
            </button>
          )}
        </div>
      </div>
      <style>{`@media (max-width: 767px) { .fiche-grid { grid-template-columns: 1fr !important; } }`}</style>
    </CommercialLayout>
  );
};

export default FicheClient;